import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { IntegrationStatus } from '@/lib/api/integrations/integrations.client'
import { cn } from '@/lib/utils'
import {
  INTEGRATIONS,
  INTEGRATION_CATEGORY_LABELS,
  INTEGRATION_SYNC_TYPE_LABELS,
  getIntegrationSyncConfig,
  formatRelativeTime,
  type IntegrationSyncType,
} from '@super/shared-types'
import { Trash2, CheckCircle2, Pencil, RefreshCw } from 'lucide-react'
import { Heading } from '@/components/ui/typography'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

function isIntegrationSyncType(
  category: string,
  syncTypes: readonly IntegrationSyncType[],
): category is IntegrationSyncType {
  return (syncTypes as readonly string[]).includes(category)
}

interface IntegrationCardProps {
  integration: (typeof INTEGRATIONS)[keyof typeof INTEGRATIONS]
  status?: IntegrationStatus
  onConnect: () => void
  onDisconnect?: () => void
  onEdit?: () => void
  onSync?: () => void
  /** Opens the sync config dialog; shown regardless of connection state. */
  onConfigureSync?: () => void
  disabledSyncTypes?: readonly IntegrationSyncType[]
  /** True until the real disabledSyncTypes has been fetched — renders sync-type tags as neutral instead of guessing enabled. */
  syncStatusLoading?: boolean
  isSyncing?: boolean
}

export function IntegrationCard({
  integration,
  status,
  onConnect,
  onDisconnect,
  onEdit,
  onSync,
  onConfigureSync,
  disabledSyncTypes = [],
  syncStatusLoading = false,
  isSyncing = false,
}: IntegrationCardProps) {
  const isConnected = status?.connected || false
  const logoPath = 'LOGO_PATH' in integration ? integration.LOGO_PATH : null

  // Integrations with URLs support editing
  const isEditable = 'URL' in integration && integration.URL

  // Check if integration supports background sync
  const syncConfig = getIntegrationSyncConfig(integration.TYPE)
  const supportsSync = syncConfig?.supportsSync ?? false

  // Get the most recent sync log (regardless of type)
  const lastSync = status?.syncLogs?.[0] // Already sorted by completedAt DESC from backend

  // Categories that aren't also a sync type render as plain tags; sync types
  // get their own clickable, enabled/disabled-colored tag instead.
  const nonSyncCategories = integration.CATEGORIES.filter(
    category =>
      !supportsSync ||
      !syncConfig ||
      !isIntegrationSyncType(category, syncConfig.syncTypes),
  )

  return (
    <Card className="p-3">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4 flex-1">
          {/* Logo */}
          <div className="w-10 h-10 rounded-lg bg-background border border-border flex items-center justify-center flex-shrink-0 overflow-hidden">
            {logoPath ? (
              <img
                src={logoPath}
                alt={integration.NAME}
                className="w-full h-full object-contain"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br flex items-center justify-center text-white font-bold text-lg">
                {integration.NAME[0]}
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <Heading variant="subsection-title" as="h3">
                {integration.NAME}
              </Heading>
              {isConnected && supportsSync && lastSync && (
                <Badge
                  variant="outline"
                  className={
                    lastSync.status === 'SUCCESS'
                      ? 'bg-success-muted text-success border-success-border'
                      : 'bg-error-muted text-error border-error-border'
                  }
                >
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  {lastSync.status === 'SUCCESS'
                    ? `Synced ${formatRelativeTime(new Date(lastSync.completedAt))}`
                    : 'Sync failed'}
                </Badge>
              )}
              {isConnected && (!supportsSync || !lastSync) && (
                <Badge
                  variant="outline"
                  className="bg-success-subtle text-success border-success"
                >
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Connected
                </Badge>
              )}
            </div>

            <p className="text-sm text-muted-foreground mb-3">
              {integration.DESCRIPTION}
            </p>

            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-2 flex-wrap">
                {nonSyncCategories.map(category => (
                  <span
                    key={category}
                    className="px-2 py-1 bg-secondary rounded"
                  >
                    {INTEGRATION_CATEGORY_LABELS[category]}
                  </span>
                ))}
                {supportsSync && (
                  <TooltipProvider delayDuration={200}>
                    {syncConfig?.syncTypes.map(syncType => {
                      const label = INTEGRATION_SYNC_TYPE_LABELS[syncType]

                      if (syncStatusLoading) {
                        return (
                          <span
                            key={syncType}
                            className="px-2 py-1 rounded bg-secondary text-muted-foreground animate-pulse"
                          >
                            {label}
                          </span>
                        )
                      }

                      if (!isConnected) {
                        return (
                          <button
                            key={syncType}
                            type="button"
                            onClick={onConfigureSync}
                            disabled={!onConfigureSync}
                            title={`${label} — not connected yet, click to configure`}
                            className="px-2 py-1 rounded bg-secondary text-muted-foreground transition-colors hover:bg-muted"
                          >
                            {label}
                          </button>
                        )
                      }

                      const isDisabled = disabledSyncTypes.includes(syncType)
                      const lastSync = status?.syncLogs?.find(
                        log => log.syncType === syncType,
                      )

                      return (
                        <Tooltip key={syncType}>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              onClick={onConfigureSync}
                              disabled={!onConfigureSync}
                              className={cn(
                                'px-2 py-1 rounded transition-colors inline-flex items-center gap-1',
                                isDisabled
                                  ? 'bg-secondary text-muted-foreground hover:bg-muted'
                                  : 'bg-info-subtle text-info hover:bg-info-muted',
                              )}
                            >
                              {!isDisabled && <RefreshCw className="h-3 w-3" />}
                              {label}
                              {isDisabled && ' (disabled)'}
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            {isDisabled
                              ? `${label} pull sync is disabled`
                              : lastSync
                                ? `Last synced ${formatRelativeTime(new Date(lastSync.completedAt))}`
                                : `${label} sync enabled — not synced yet`}
                          </TooltipContent>
                        </Tooltip>
                      )
                    })}
                  </TooltipProvider>
                )}
              </div>
              {isConnected && status?.connectedAt && (
                <span>
                  Connected {new Date(status.connectedAt).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="ml-4 flex items-center gap-2">
          {isConnected ? (
            <>
              {supportsSync && onSync && (
                <Button
                  onClick={onSync}
                  variant="ghost"
                  size="icon"
                  disabled={isSyncing}
                  className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-muted"
                  title={isSyncing ? 'Syncing...' : 'Sync now'}
                >
                  <RefreshCw
                    className={`h-3.5 w-3.5 ${isSyncing ? 'animate-spin' : ''}`}
                  />
                </Button>
              )}
              {isEditable && onEdit && (
                <Button
                  onClick={onEdit}
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-muted"
                  title="Edit credentials"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              )}
              <Button
                onClick={onDisconnect}
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-error-dim hover:text-error hover:bg-error-subtle"
                title="Disconnect"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </>
          ) : (
            <Button onClick={onConnect} variant="default">
              Connect
            </Button>
          )}
        </div>
      </div>
    </Card>
  )
}
